import { useState } from 'react';
import {
  Webhook,
  Plus,
  Pencil,
  Trash2,
  Play,
  Loader2,
  CheckCircle2,
  XCircle,
  Copy,
  RefreshCw,
} from 'lucide-react';
import {
  useWebhooks,
  useCreateWebhook,
  useUpdateWebhook,
  useDeleteWebhook,
  useTestWebhook,
  type Webhook as WebhookType,
  type WebhookEvent,
  type WebhookTestResult,
} from '../hooks/useWebhooks';
import { Badge } from '../components/shared/Badge';
import { EmptyState } from '../components/shared/EmptyState';
import { cn } from '../lib/cn';

interface WebhooksViewProps {
  onNavigate: (view: string, params?: Record<string, string>) => void;
}

const allEvents: { value: WebhookEvent; label: string; group: string }[] = [
  { value: 'contact.created', label: 'Contact Created', group: 'Contacts' },
  { value: 'contact.updated', label: 'Contact Updated', group: 'Contacts' },
  { value: 'contact.deleted', label: 'Contact Deleted', group: 'Contacts' },
  { value: 'organization.created', label: 'Organization Created', group: 'Organizations' },
  { value: 'organization.updated', label: 'Organization Updated', group: 'Organizations' },
  { value: 'deal.created', label: 'Deal Created', group: 'Deals' },
  { value: 'deal.updated', label: 'Deal Updated', group: 'Deals' },
  { value: 'deal.stage_changed', label: 'Deal Stage Changed', group: 'Deals' },
  { value: 'deal.won', label: 'Deal Won', group: 'Deals' },
  { value: 'deal.lost', label: 'Deal Lost', group: 'Deals' },
  { value: 'task.completed', label: 'Task Completed', group: 'Tasks' },
  { value: 'case.created', label: 'Case Created', group: 'Cases' },
  { value: 'case.closed', label: 'Case Closed', group: 'Cases' },
  { value: 'lead.created', label: 'Lead Created', group: 'Leads' },
  { value: 'lead.converted', label: 'Lead Converted', group: 'Leads' },
];

type ViewMode = 'list' | 'form' | 'test-result';

// --- Webhook Form ---

interface WebhookFormProps {
  initial?: WebhookType;
  onSave: (data: Partial<WebhookType>) => void;
  onCancel: () => void;
  isSaving: boolean;
}

function WebhookForm({ initial, onSave, onCancel, isSaving }: WebhookFormProps) {
  const [url, setUrl] = useState(initial?.url ?? '');
  const [events, setEvents] = useState<WebhookEvent[]>(initial?.events ?? []);
  const [secret, setSecret] = useState(initial?.secret ?? '');
  const [isActive, setIsActive] = useState(initial?.isActive ?? true);

  function toggleEvent(event: WebhookEvent) {
    setEvents((prev) =>
      prev.includes(event) ? prev.filter((e) => e !== event) : [...prev, event],
    );
  }

  function generateSecret() {
    const arr = new Uint8Array(32);
    crypto.getRandomValues(arr);
    setSecret(Array.from(arr, (b) => b.toString(16).padStart(2, '0')).join(''));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    onSave({ url, events, secret: secret || undefined, isActive });
  }

  // Group events by category
  const grouped = allEvents.reduce<Record<string, typeof allEvents>>((acc, ev) => {
    (acc[ev.group] ??= []).push(ev);
    return acc;
  }, {});

  return (
    <form onSubmit={handleSubmit} className="space-y-6 p-6">
      <div className="card p-5 space-y-4">
        <h3 className="text-sm font-semibold text-slate-900">Webhook Configuration</h3>
        <div>
          <label className="label">Endpoint URL</label>
          <input
            type="url"
            className="input"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://example.com/webhook"
            required
          />
        </div>
        <div>
          <label className="label">Secret Key</label>
          <div className="flex gap-2">
            <input
              type="text"
              className="input flex-1"
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              placeholder="Optional signing secret"
            />
            <button type="button" onClick={generateSecret} className="btn-secondary btn-sm">
              <RefreshCw className="h-3.5 w-3.5" />
              Generate
            </button>
          </div>
          <p className="mt-1 text-2xs text-slate-400">
            Used to sign webhook payloads via HMAC-SHA256.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="webhook-active"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            className="h-4 w-4 rounded border-surface-300"
          />
          <label htmlFor="webhook-active" className="text-sm text-slate-700">Active</label>
        </div>
      </div>

      <div className="card p-5 space-y-4">
        <h3 className="text-sm font-semibold text-slate-900">Events</h3>
        <p className="text-xs text-slate-500">Select the events that will trigger this webhook.</p>
        <div className="space-y-4">
          {Object.entries(grouped).map(([group, groupEvents]) => (
            <div key={group}>
              <div className="text-xs font-medium text-slate-500 uppercase mb-2">{group}</div>
              <div className="flex flex-wrap gap-2">
                {groupEvents.map((ev) => (
                  <label
                    key={ev.value}
                    className={cn(
                      'flex items-center gap-2 rounded-lg border px-3 py-2 text-sm cursor-pointer transition-colors',
                      events.includes(ev.value)
                        ? 'border-brand-300 bg-brand-50 text-brand-700'
                        : 'border-surface-200 text-slate-600 hover:border-surface-300',
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={events.includes(ev.value)}
                      onChange={() => toggleEvent(ev.value)}
                      className="sr-only"
                    />
                    {ev.label}
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-end gap-3">
        <button type="button" onClick={onCancel} className="btn-secondary">Cancel</button>
        <button
          type="submit"
          className="btn-primary"
          disabled={isSaving || !url.trim() || events.length === 0}
        >
          {isSaving ? 'Saving...' : initial ? 'Update Webhook' : 'Create Webhook'}
        </button>
      </div>
    </form>
  );
}

// --- Main View ---

export function WebhooksView({ onNavigate }: WebhooksViewProps) {
  const [mode, setMode] = useState<ViewMode>('list');
  const [editingWebhook, setEditingWebhook] = useState<WebhookType | null>(null);
  const [testResult, setTestResult] = useState<WebhookTestResult | null>(null);
  const [testingId, setTestingId] = useState<string | null>(null);

  const { data, isLoading, error } = useWebhooks();
  const createWebhook = useCreateWebhook();
  const updateWebhook = useUpdateWebhook();
  const deleteWebhook = useDeleteWebhook();
  const testWebhook = useTestWebhook();

  const webhooks = data?.webhooks ?? [];

  function handleCreate() {
    setEditingWebhook(null);
    setMode('form');
  }

  function handleEdit(wh: WebhookType) {
    setEditingWebhook(wh);
    setMode('form');
  }

  function handleSave(formData: Partial<WebhookType>) {
    if (editingWebhook) {
      updateWebhook.mutate(
        { id: editingWebhook.id, data: formData },
        { onSuccess: () => setMode('list') },
      );
    } else {
      createWebhook.mutate(formData, { onSuccess: () => setMode('list') });
    }
  }

  function handleTest(id: string) {
    setTestingId(id);
    testWebhook.mutate(id, {
      onSuccess: (result) => {
        setTestResult(result);
        setMode('test-result');
      },
      onSettled: () => setTestingId(null),
    });
  }

  function handleDelete(id: string) {
    deleteWebhook.mutate(id);
  }

  // --- Test Result ---
  if (mode === 'test-result' && testResult) {
    return (
      <div className="flex-1 overflow-y-auto">
        <div className="page-header">
          <div>
            <h1 className="text-xl font-bold text-slate-900">Webhook Test Result</h1>
            <p className="mt-0.5 text-sm text-slate-500">Response from the endpoint</p>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <button
            onClick={() => setMode('list')}
            className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
          >
            &larr; Back to webhooks
          </button>

          <div className="card p-5 space-y-3">
            <div className="flex items-center gap-3">
              {testResult.error ? (
                <XCircle className="h-6 w-6 text-red-500" />
              ) : testResult.statusCode < 400 ? (
                <CheckCircle2 className="h-6 w-6 text-green-500" />
              ) : (
                <XCircle className="h-6 w-6 text-red-500" />
              )}
              <div>
                <div className="text-sm font-medium text-slate-900">
                  {testResult.error ? 'Connection Failed' : `HTTP ${testResult.statusCode}`}
                </div>
                <div className="text-xs text-slate-500">{testResult.responseTime}ms</div>
              </div>
            </div>

            {testResult.error && (
              <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{testResult.error}</div>
            )}

            {testResult.responseBody && (
              <div>
                <div className="text-xs font-medium text-slate-500 mb-1">Response Body</div>
                <pre className="rounded-lg bg-slate-900 p-4 text-xs text-slate-100 overflow-x-auto">
                  {testResult.responseBody}
                </pre>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // --- Form Mode ---
  if (mode === 'form') {
    return (
      <div className="flex-1 overflow-y-auto">
        <div className="page-header">
          <div>
            <h1 className="text-xl font-bold text-slate-900">
              {editingWebhook ? 'Edit Webhook' : 'New Webhook'}
            </h1>
            <p className="mt-0.5 text-sm text-slate-500">
              {editingWebhook ? 'Update webhook configuration' : 'Register a new webhook endpoint'}
            </p>
          </div>
        </div>
        <WebhookForm
          initial={editingWebhook ?? undefined}
          onSave={handleSave}
          onCancel={() => setMode('list')}
          isSaving={createWebhook.isPending || updateWebhook.isPending}
        />
      </div>
    );
  }

  // --- List Mode ---
  return (
    <div className="flex-1 overflow-y-auto">
      <div className="page-header">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Webhooks</h1>
          <p className="mt-0.5 text-sm text-slate-500">
            {data ? `${data.total} webhook${data.total !== 1 ? 's' : ''}` : 'Webhook integrations'}
          </p>
        </div>
        <button className="btn-primary" onClick={handleCreate}>
          <Plus className="h-4 w-4" />
          New Webhook
        </button>
      </div>

      <div className="p-6">
        {error ? (
          <div className="card p-8 text-center text-sm text-red-600">
            Failed to load webhooks. Please try again.
          </div>
        ) : isLoading ? (
          <div className="card divide-y divide-surface-100">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4 px-4 py-4">
                <div className="h-4 w-64 animate-pulse rounded bg-surface-200" />
                <div className="h-4 w-24 animate-pulse rounded bg-surface-100" />
              </div>
            ))}
          </div>
        ) : webhooks.length > 0 ? (
          <div className="card divide-y divide-surface-100">
            <div className="grid grid-cols-12 gap-4 px-4 py-2 text-xs font-medium text-slate-500 bg-surface-50">
              <div className="col-span-4">URL</div>
              <div className="col-span-2">Events</div>
              <div className="col-span-1">Status</div>
              <div className="col-span-2">Last Triggered</div>
              <div className="col-span-3 text-right">Actions</div>
            </div>
            {webhooks.map((wh) => (
              <div key={wh.id} className="grid grid-cols-12 gap-4 items-center px-4 py-3">
                <div className="col-span-4 text-sm text-slate-900 font-mono truncate" title={wh.url}>
                  {wh.url}
                </div>
                <div className="col-span-2 text-sm text-slate-600">
                  {wh.events.length} event{wh.events.length !== 1 ? 's' : ''}
                </div>
                <div className="col-span-1">
                  <Badge color={wh.isActive ? 'green' : 'gray'} size="sm">
                    {wh.isActive ? 'Active' : 'Off'}
                  </Badge>
                </div>
                <div className="col-span-2 text-xs text-slate-400">
                  {wh.lastTriggeredAt
                    ? new Date(wh.lastTriggeredAt).toLocaleString()
                    : 'Never'}
                  {wh.lastStatus && (
                    <span
                      className={cn(
                        'ml-1',
                        wh.lastStatus < 400 ? 'text-green-500' : 'text-red-500',
                      )}
                    >
                      ({wh.lastStatus})
                    </span>
                  )}
                </div>
                <div className="col-span-3 flex items-center justify-end gap-1">
                  <button
                    onClick={() => handleTest(wh.id)}
                    className="rounded p-1.5 text-slate-400 hover:bg-surface-100 hover:text-slate-600"
                    title="Test webhook"
                    disabled={testingId === wh.id}
                  >
                    {testingId === wh.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Play className="h-4 w-4" />
                    )}
                  </button>
                  <button
                    onClick={() => handleEdit(wh)}
                    className="rounded p-1.5 text-slate-400 hover:bg-surface-100 hover:text-slate-600"
                    title="Edit"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(wh.id)}
                    className="rounded p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-500"
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState
            icon={Webhook}
            title="No webhooks"
            description="Create a webhook to send real-time notifications to external services."
            actionLabel="Create Webhook"
            onAction={handleCreate}
          />
        )}
      </div>
    </div>
  );
}
